import React, { useMemo, useState } from "react";
import { connect } from "react-redux";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import Role from "@mapmycustomers/shared/types/Role";
import { Button, Popconfirm, PopconfirmProps } from "@mapmycustomers/ui";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import settingsAnalytics from "@app/scene/settings/settingsAnaytics";
import useMenu from "@app/scene/settings/util/useMenu";
import {
  doesUserHaveAccessToDupeCheck,
  doesUserHaveAccessToImports,
  isCurrentUserManager,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import MenuItem from "@app/types/MenuItem";

import Menu from "../menu";

import AnonymousSwitcher from "./AnonymousSwitcher";
import styles from "./Header.module.scss";

interface Props {
  changesSaving?: boolean;
  doesUserHaveAccessToDupeCheck: boolean;
  doesUserHaveAccessToImports: boolean;
  hasChanges?: boolean;
  isManager: boolean;
  isOwner: boolean;
  isReadOnlyMember: boolean;
  onSaveChanges?: () => void;
  onUnDo?: () => void;
  saveConfirmation?: PopconfirmProps;
  shouldManualClearOnLeave?: boolean;
  userRole?: Role;
}

const Header: React.FC<Props> = ({
  changesSaving,
  doesUserHaveAccessToDupeCheck,
  doesUserHaveAccessToImports,
  hasChanges,
  isManager,
  isOwner,
  isReadOnlyMember,
  onSaveChanges,
  onUnDo,
  saveConfirmation,
  shouldManualClearOnLeave,
}) => {
  const intl = useIntl();
  const [visible, setVisibility] = useState(false);
  const { pathname } = useLocation();
  const menuCategories = useMenu(
    intl,
    isOwner,
    isManager,
    isReadOnlyMember,
    doesUserHaveAccessToDupeCheck,
    doesUserHaveAccessToImports
  );

  const buttonName = useMemo(
    () =>
      menuCategories
        ?.reduce<MenuItem[]>((items, category) => [...items, ...(category.items ?? [])], [])
        .find(({ path }) => (path ? pathname.includes(path) : false))?.label ?? "",
    [menuCategories, pathname]
  );

  return (
    <div className={styles.container}>
      <div className={styles.subContainer}>
        {intl.formatMessage({
          id: "settings.header.title",
          defaultMessage: "Settings",
          description: "Title of settings page",
        })}
        <Dropdown
          className={styles.menuButton}
          onOpenChange={setVisibility}
          open={visible}
          overlay={
            <div className={styles.menuContainer}>
              <Menu onClick={() => setVisibility(false)} />
            </div>
          }
          overlayClassName={styles.dropdownOverlay}
          trigger={["click"]}
        >
          <Button rightIcon={<FontAwesomeIcon icon={faChevronDown} />}>{buttonName}</Button>
        </Dropdown>
      </div>

      <Row gutter={layout.spacerL}>
        <Col>
          <AnonymousSwitcher />
        </Col>
        {hasChanges && (
          <Col>
            <Row gutter={layout.spacerM}>
              <Col>
                <Button disabled={changesSaving} onClick={onUnDo}>
                  {intl.formatMessage({
                    id: "settings.header.undo",
                    defaultMessage: "Undo",
                    description: "Undo button of settings page",
                  })}
                </Button>
              </Col>
              <Col>
                <Popconfirm
                  // this title will be overridden by the one in saveConfirmation because it is required there
                  description={""}
                  trigger={saveConfirmation ? ["click"] : []}
                  {...saveConfirmation}
                >
                  <Button
                    disabled={changesSaving}
                    loading={changesSaving}
                    onClick={saveConfirmation ? undefined : onSaveChanges}
                    type="primary"
                  >
                    {intl.formatMessage({
                      id: "settings.header.saveChanges",
                      defaultMessage: "Save Changes",
                      description: "Save Changes button of settings page",
                    })}
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
      <InterceptNavigationModal
        analyticIssuer={settingsAnalytics}
        onLeave={onUnDo}
        shouldCloseOnLeave={shouldManualClearOnLeave}
        shouldShowPrompt={!!hasChanges}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  doesUserHaveAccessToDupeCheck: doesUserHaveAccessToDupeCheck(state),
  doesUserHaveAccessToImports: doesUserHaveAccessToImports(state),
  isManager: isCurrentUserManager(state),
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

export default connect(mapStateToProps)(Header);
